import {
  Building2,
  Inbox,
  Landmark,
  ReceiptEuro,
  Search,
  SendHorizonal,
  Settings,
  ShoppingCart,
  UserRoundCog,
  Wrench,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSession } from "@/lib/auth/auth";
import { useParams } from "@tanstack/react-router";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { usePermissions } from "@/hooks/usePermissions";
import { SuperadminTools } from "@/features/developer-tools";
import useLedgerFeatureFlag from "@/hooks/use-ledger-feature-flag";

import {
  PayableTodosBadge,
  ReceivableTodosBadge,
} from "./badges/accounting-firm";
import {
  AccountingTransactionsTodosBadge,
  PayableInvoicesToReviewBadge,
  TransactionsBadge,
  TransfersBadge,
} from "./badges/company";

type SidebarItem = {
  text: string;
  icon?: React.ElementType;
  path?: string;
  badge?: React.ElementType;
  component?: React.ReactNode;
  submenu?: Array<{
    text: string;
    icon: React.ElementType;
    path: string;
  }>;
};

export const useAccountingSidebarItems = () => {
  const params = useParams({ from: "/_authenticated/$company" });
  const { t } = useTranslation();
  const { data: session } = useSession();
  const companySlug = params.company;
  const { isSuperAdmin } = usePermissions();

  const { isLedgerEnabled } = useLedgerFeatureFlag();

  const sidebarItems = useMemo(() => {
    const items: SidebarItem[] = companySlug
      ? [
          {
            text: t("todos.payable.title") as string,
            icon: ShoppingCart,
            path: `/${companySlug}/buchhaltung/eingang`,
            badge: PayableTodosBadge,
          },
          {
            text: t("todos.receivable.title") as string,
            icon: ReceiptEuro,
            path: `/${companySlug}/buchhaltung/ausgang`,
            badge: ReceivableTodosBadge,
          },
          {
            text: t("all_invoices") as string,
            icon: Search,
            path: `/${companySlug}/buchhaltung/rechnungen`,
          },
          ...(isLedgerEnabled
            ? [
                {
                  text: t("bank.link") as string,
                  icon: Landmark,
                  path: `/${companySlug}/buchhaltung/transaktionen/todos`,
                  badge: AccountingTransactionsTodosBadge,
                },
              ]
            : []),
          {
            text: t("setup") as string,
            icon: Settings,
            path: `/${companySlug}/buchhaltung/einstellungen/eckdaten`,
          },
        ]
      : [];

    return items;
  }, [companySlug, t, session]);

  const footerItems = useMemo(() => {

    if (isSuperAdmin) {
      return ([
        {
          text: "Superadmin Tools",
          component: (
            <SuperadminTools>
              <SidebarMenuButton>
                <Wrench className="text-primary" size={18} />
                <p className="whitespace-nowrap">Superadmin Tools</p>
              </SidebarMenuButton>
            </SuperadminTools>
          ),
        }
      ])
    }

    return [];
  }, []);

  return {
    sidebarItems,
    footerItems,
  };
};

export const useCompanySidebarItems = () => {
  const params = useParams({ from: "/_authenticated/$company" });
  const { t } = useTranslation();
  const { data: session } = useSession();
  const companySlug = params.company;

  const sidebarItems = useMemo(() => {
    const items: SidebarItem[] = companySlug
      ? [
          {
            text: t("bank.link") as string,
            icon: Landmark,
            path: `/${companySlug}/unternehmen/transaktionen/todos`,
            badge: TransactionsBadge,
          },
          {
            text: t("payables.link") as string,
            icon: Inbox,
            path: `/${companySlug}/unternehmen/belege`,
            badge: PayableInvoicesToReviewBadge,
          },
          {
            text: t("receivables.link") as string,
            icon: ReceiptEuro,
            path: `/${companySlug}/unternehmen/ausgangsrechnungen`,
          },
          {
            text: t("transfers.link") as string,
            icon: SendHorizonal,
            path: `/${companySlug}/unternehmen/ueberweisungen`,
            badge: TransfersBadge,
          },
          {
            text: t("setup") as string,
            icon: Settings,
            path: `/${companySlug}/unternehmen/einstellungen/bank`,
          },
        ]
      : [];

    return items;
  }, [companySlug, t, session]);

  const footerItems = useMemo(() => {
    return [];
  }, []);

  return {
    sidebarItems,
    footerItems,
  };
};

export const useAccountingFirmSidebarItems = () => {
  const { isAccountingFirmAdmin } = usePermissions();

  const items = [
    {
      text: "Mandantenübersicht",
      icon: Building2,
      path: "/",
    },
  ];

  if (isAccountingFirmAdmin) {
    items.push({
      text: "Nutzer verwalten",
      icon: UserRoundCog,
      path: "/kanzlei/nutzer",
    });
  }

  return {
    sidebarItems: items,
    footerItems: [],
  };
};
